import { Box, Grid, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { enUS, pl } from "date-fns/locale";
import { useCallback } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SellerSelect } from "../../../../components/form/SellerSelect/SellerSelect";
import { useLanguage } from "../../../../utils";
import AgreementBankingFormControl from "./AgreementBankingFormControl";
import { AgreementKindEnum } from "../../../../schemas/agreements";
import { BrokerInputForm } from "../../../../components/form/BrokerInputForm/BrokerInputForm";

export function AgreementDetailsFormControl(props) {
    const { fields, prolongateMode = false, readonly = false, agreementKind, investmentId, productIds, productDiscounts } = props;
    const { t } = useTranslation();
    const language = useLanguage();
    const { control, setValue } = useFormContext();

    const caretakerId = useWatch({ name: fields.caretakerId });
    const setCaretakerId = useCallback(
        (sellerId) => {
            setValue(fields.caretakerId, sellerId);
        },
        [setValue, fields.caretakerId],
    );

    const banking = useWatch({ name: fields.agreement_banking });
    const setBanking = useCallback(
        (banking) => {
            setValue(fields.agreement_banking, banking);
        },
        [setValue, fields.agreement_banking],
    );

    const bankingForWithdrawal = useWatch({ name: fields.agreement_banking_withdrawal });
    const setBankingForWithdrawal = useCallback(
        (banking) => {
            setValue(fields.agreement_banking_withdrawal, banking);
        },
        [setValue, fields.agreement_banking_withdrawal],
    );

    const isReservationAgreement = agreementKind?.agreementType === AgreementKindEnum.RESERVATION
    const disabled = readonly || prolongateMode; // Everything except expiry date

    return (
        <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={language === "pl" ? pl : enUS}
        >
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <SellerSelect
                            label={`${t("agreements.details.caretaker")}*`}
                            selectedSellerId={caretakerId}
                            setSelectedSellerId={setCaretakerId}
                            disabled={disabled}
                        />
                        <Controller
                            name={fields.notarialDeedNumber}
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t("agreements.details.notarialDeedNumber")}
                                    fullWidth
                                    disabled={disabled}
                                />
                            )}
                        />
                        <Controller
                            name={fields.notarialDeedSignDate}
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    label={t("agreements.details.notarialDeedSignDate")}
                                    value={field.value || null}
                                    onChange={field.onChange}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    disabled={disabled}
                                />
                            )}
                        />
                        <Controller
                            name={fields.plannedAgreementSignDate}
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    label={t("agreements.details.plannedAgreementSignDate")}
                                    value={field.value || null}
                                    onChange={field.onChange}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    disabled={disabled}
                                />
                            )}
                        />
                        <Controller
                            name={fields.agreementSignDate}
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    label={t("agreements.details.agreementSignDate")}
                                    value={field.value || null}
                                    onChange={field.onChange}
                                    maxDate={new Date()}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    disabled={disabled}
                                />
                            )}
                        />
                        <Controller
                            name={fields.agreementReceptionDate}
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    label={t("agreements.details.receptionDate")}
                                    value={field.value || null}
                                    onChange={field.onChange}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    disabled={readonly}
                                />
                            )}
                        />
                        <Controller
                            name={fields.agreementExpiryDate}
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    label={t("agreements.details.agreementExpiryDate")}
                                    value={field.value || null}
                                    onChange={field.onChange}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    disabled={readonly}
                                />
                            )}
                        />
                        <Controller
                            name={fields.agreementBrokerName}
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t("agreements.details.agreementBrokerName")}
                                    fullWidth
                                    disabled={disabled}
                                />
                            )}
                        />
                        <BrokerInputForm 
                            fields={fields} 
                            productIds={productIds} 
                            productDiscounts={productDiscounts} 
                            readonly={disabled}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box display="flex" flexDirection="column" gap={2} height="100%">
                        <Controller
                            name={fields.archiveNumber}
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t("agreements.details.archiveNumber")}
                                    fullWidth
                                    disabled={disabled}
                                />
                            )}
                        />
                        <Controller
                            name={fields.isOperator}
                            control={control}
                            render={({ field }) => (
                                <Box display="flex" alignItems="center" gap={2} sx={{ p: 0.5 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{ minWidth: "50%", textAlign: "start", px: 1.5 }}
                                    >
                                        {t("agreements.details.isOperator")}
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={field.value ? "yes" : "no"}
                                        exclusive
                                        onChange={(_, value) => {
                                            if (value != null) {
                                                field.onChange(value === "yes");
                                            }
                                        }}
                                        fullWidth
                                    >
                                        <ToggleButton value="yes">{t("common.yes")}</ToggleButton>
                                        <ToggleButton value="no">{t("common.no")}</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            )}
                        />

                        <Controller
                            name={fields.isWpk}
                            control={control}
                            render={({ field }) => (
                                <Box display="flex" alignItems="center" gap={2} sx={{ p: 0.5 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{ minWidth: "50%", textAlign: "start", px: 1.5 }}
                                    >
                                        {t("agreements.details.isWpk")}
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={field.value ? "yes" : "no"}
                                        exclusive
                                        onChange={(_, value) => {
                                            if (value != null) {
                                                field.onChange(value === "yes");
                                            }
                                        }}
                                        fullWidth
                                    >
                                        <ToggleButton value="yes">{t("common.yes")}</ToggleButton>
                                        <ToggleButton value="no">{t("common.no")}</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            )}
                        />

                        <Controller
                            name={fields.isLoan}
                            control={control}
                            render={({ field }) => (
                                <Box display="flex" alignItems="center" gap={2} sx={{ p: 0.5 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{ minWidth: "50%", textAlign: "start", px: 1.5 }}
                                    >
                                        {t("agreements.details.isLoan")}
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={field.value ? "yes" : "no"}
                                        exclusive
                                        onChange={(_, value) => {
                                            if (value != null) {
                                                field.onChange(value === "yes");
                                            }
                                        }}
                                        fullWidth
                                    >
                                        <ToggleButton value="yes">{t("common.yes")}</ToggleButton>
                                        <ToggleButton value="no">{t("common.no")}</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            )}
                        />

                        {!isReservationAgreement && <AgreementBankingFormControl isBankingForWithdrawal={false} banking={banking} readOnly={disabled} onSelectBanking={setBanking} investmentId={investmentId}/>}
                        {!isReservationAgreement && <AgreementBankingFormControl isBankingForWithdrawal={true} banking={bankingForWithdrawal} readOnly={disabled} onSelectBanking={setBankingForWithdrawal} investmentId={investmentId}/>}
                        <Controller
                            name={fields.note}
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t("agreements.details.note")}
                                    fullWidth
                                    multiline
                                    rows={7}
                                    disabled={disabled}
                                />
                            )}
                        />
                    </Box>
                </Grid>
            </Grid>
        </LocalizationProvider>
    );
}
